import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation, mapPublishProductError, buildProductUrl } from '@dukanti/shared';
import type { PublishProductErrorKey } from '@dukanti/shared';
import { useUnpublishProduct } from '@/hooks/use-unpublish-product';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface UnpublishProductDialogProps {
  readonly productId: string;
  readonly storeSlug: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for hiding (unpublishing) a single product (F-028).
 *
 * Mirror of `UnpublishStoreDialog` with the **asymmetric button
 * layout** as a load-bearing UX nudge (F-028 Decision 12): the PRIMARY
 * action is "Keep visible" (the reversal) rendered in Za'atar Olive
 * — the color of the happy path. The SECONDARY action is "Hide
 * product" rendered as a neutral ghost-with-border button. This
 * asymmetry guides the merchant toward the happy path if the dialog
 * was opened by mistake; recoloring "Hide" red would imply data loss
 * and discourage legitimate pauses.
 *
 * Since the RPC skips gates 4 and 5 on unpublish (publish = false),
 * the only error branches that can be reached at runtime are
 * `permissionDenied`, `notFound`, `networkFailed`, and `unknown`. The
 * `storeNotPublished` and `insufficientInventory` branches are still
 * rendered defensively with v8-ignore markers so the banner never
 * crashes on an unexpected mapping outcome.
 */
export function UnpublishProductDialog({
  productId,
  storeSlug,
  open,
  onOpenChange,
}: UnpublishProductDialogProps) {
  const { t, dir } = useTranslation();
  const unpublishMutation = useUnpublishProduct();
  const [errorKey, setErrorKey] = useState<PublishProductErrorKey | null>(null);

  const isPending = unpublishMutation.isPending;
  const publicUrl = buildProductUrl({ storeSlug, productId });

  useEffect(() => {
    if (!open) {
      setErrorKey(null);
    }
  }, [open]);

  function handleOpenChange(next: boolean): void {
    if (isPending) return;
    if (!next) setErrorKey(null);
    onOpenChange(next);
  }

  function handleConfirm(): void {
    if (isPending) return;
    setErrorKey(null);
    unpublishMutation.mutate(
      { productId },
      {
        onSuccess: () => {
          toast.success(t('admin.products.publish.toasts.unpublished'));
          onOpenChange(false);
        },
        onError: (err) => {
          const mapped = mapPublishProductError(err);
          setErrorKey(mapped);
          toast.error(t(mapped));
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent dir={dir} data-testid="unpublish-product-dialog" className="gap-0 p-8">
        {/* Icon wrap — neutral Sand background, Sand foreground. The
            EyeOff glyph reads as "hidden", not "destroyed". */}
        <div
          aria-hidden="true"
          className="bg-sand-100 text-sand-500 mx-auto mb-4 flex size-14 items-center justify-center rounded-full"
        >
          <EyeOff className="size-7" strokeWidth={1.75} />
        </div>

        <AlertDialogHeader className="mb-3">
          <AlertDialogTitle className="text-center">
            {t('admin.products.publish.unpublishModal.title')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogDescription className="text-sand-600 mb-2 text-center text-sm leading-relaxed">
          {t('admin.products.publish.unpublishModal.description')}
        </AlertDialogDescription>

        <p className="text-sand-500 mb-2 text-center text-xs">
          {t('admin.products.publish.unpublishModal.urlLabel')}
        </p>

        <div
          data-testid="product-public-url-preview"
          dir="ltr"
          className="border-sand-200 bg-sand-100 text-sand-500 mb-6 truncate rounded-lg border px-4 py-3 text-center font-mono text-sm line-through"
        >
          {publicUrl}
        </div>

        {errorKey ? (
          <div
            role="alert"
            data-testid="unpublish-product-dialog-error"
            data-error-key={errorKey}
            className={cn(
              'border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card mb-6',
              'flex items-start gap-2 border p-3 text-start text-sm',
            )}
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <div className="flex-1">
              {errorKey === 'admin.products.publish.errors.permissionDenied' ? (
                <p data-testid="unpublish-product-dialog-error-permissionDenied">
                  {t('admin.products.publish.errors.permissionDenied')}
                </p>
              ) : errorKey === 'admin.products.publish.errors.notFound' ? (
                <p data-testid="unpublish-product-dialog-error-notFound">
                  {t('admin.products.publish.errors.notFound')}
                </p>
              ) : errorKey === 'admin.products.publish.errors.networkFailed' ? (
                <p data-testid="unpublish-product-dialog-error-networkFailed">
                  {t('admin.products.publish.errors.networkFailed')}
                </p>
              ) : errorKey === 'admin.products.publish.errors.storeNotPublished' ? (
                /* v8 ignore next 3 — unreachable: RPC skips the store-published
                   gate when publish=false. Rendered defensively. */
                <p data-testid="unpublish-product-dialog-error-storeNotPublished">
                  {t('admin.products.publish.errors.storeNotPublished')}
                </p>
              ) : errorKey === 'admin.products.publish.errors.insufficientInventory' ? (
                /* v8 ignore next 3 — unreachable: RPC skips the inventory
                   gate when publish=false. Rendered defensively. */
                <p data-testid="unpublish-product-dialog-error-insufficientInventory">
                  {t('admin.products.publish.errors.insufficientInventory')}
                </p>
              ) : (
                <p data-testid="unpublish-product-dialog-error-unknown">
                  {t('admin.products.publish.errors.unknown')}
                </p>
              )}
            </div>
          </div>
        ) : null}

        <AlertDialogFooter className="flex-col-reverse gap-2 sm:flex-col-reverse sm:space-x-0">
          {/* Secondary: confirm unpublish — neutral ghost with Sand
              border. NOT destructive red. */}
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={handleConfirm}
            data-testid="unpublish-product-dialog-confirm"
            aria-busy={isPending || undefined}
            className="border-sand-200 text-sand-500 hover:bg-sand-100 hover:text-sand-700 w-full border"
          >
            {isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                <span>{t('admin.products.publish.buttons.unpublishing')}</span>
              </>
            ) : (
              <>
                <EyeOff aria-hidden="true" className="size-4" strokeWidth={1.75} />
                <span>{t('admin.products.publish.unpublishModal.confirm')}</span>
              </>
            )}
          </Button>
          {/* Primary: stay visible — Za'atar Olive. The reversal is
              the loud happy-path choice, not the destructive one. */}
          <Button
            type="button"
            variant="default"
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
            data-testid="unpublish-product-dialog-stay"
            className="w-full"
          >
            {t('admin.products.publish.unpublishModal.stay')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
